import {isTextSelection, posToDOMRect} from '@tiptap/core'
import {PluginView} from '@tiptap/pm/state'
import {Plugin, PluginKey} from 'prosemirror-state'
import {EditorView} from 'prosemirror-view'
import {BlockNoteEditor} from '../../BlockNoteEditor'
import {EventEmitter} from '../../shared/EventEmitter'
import {BlockSchema} from '../Blocks/api/blockTypes'
import {getNearestBlockOrCellPos} from '../Blocks/helpers/getBlockInfoFromPos'

/**
 * The state emitted to React subscribers whenever the text selection changes in
 * read-only mode.  `show` is false when nothing is selected or the editor is
 * editable (edit mode has its own formatting toolbar).
 */
export type RangeSelectionState = {
  show: boolean
  /** The `data-id` of the blockNode that contains the selection start. */
  blockId: string | null
  /** Unicode codepoint offset of the selection start within the block's text content. */
  rangeStart: number | null
  /** Unicode codepoint offset of the selection end within the block's text content. */
  rangeEnd: number | null
  /** Bounding rect of the selection, used by the positioner to place the bubble. */
  referenceRect: DOMRect | null
}

/** Internal EventEmitter event map for the range-selection plugin. */
type RangeSelectionEvents = {
  update: [RangeSelectionState]
}

const SETTLE_DELAY_MS = 10

/**
 * Converts a ProseMirror document position into a zero-based Unicode
 * codepoint offset relative to the start of a block content node.
 *
 * Text nodes count by Unicode codepoint, while non-text inline nodes (for
 * example inline embeds) count as one codepoint. This mirrors fragment-link
 * rendering so edit-mode toolbar actions and loaded/read-only selections
 * produce identical block ranges.
 */
export function prosemirrorPosToBlockTextOffset(
  doc: import('prosemirror-model').Node,
  docPos: number,
  blockContentBeforePos: number,
): number {
  const blockContentNode = doc.resolve(blockContentBeforePos + 1).parent

  // docPos is an absolute position. The start of blockContent's first child
  // sits at blockContentBeforePos + 1 (the opening token of blockContent).
  const targetOffset = docPos - (blockContentBeforePos + 1)

  let codepoints = 0
  let done = false

  // Walk each inline child of the blockContent node and accumulate
  // codepoints until `targetOffset` (in UTF-16 units within the parent
  // content) falls inside or just before the current child.
  //
  // `nodeOffset` is the cumulative UTF-16 offset of `node` within
  // blockContent — it stays absolute, so we compare `targetOffset`
  // against it directly instead of decrementing as we go. This keeps
  // ranges stable when marks or inline embeds split the paragraph into
  // multiple inline children.
  blockContentNode.forEach((node, nodeOffset) => {
    if (done) return
    const nodeEnd = nodeOffset + node.nodeSize

    if (node.isText && node.text) {
      if (targetOffset >= nodeEnd) {
        codepoints += Array.from(node.text).length
      } else if (targetOffset > nodeOffset) {
        const slice = node.text.slice(0, targetOffset - nodeOffset)
        codepoints += Array.from(slice).length
        done = true
      } else {
        done = true
      }
    } else {
      if (targetOffset >= nodeEnd) {
        codepoints += 1
      } else {
        done = true
      }
    }
  })

  return codepoints
}

/**
 * Internal ProseMirror PluginView that watches selection changes and emits
 * {@link RangeSelectionState} to React whenever there is a non-empty text
 * selection while the editor is read-only.
 *
 * @internal
 */
class RangeSelectionView<BSchema extends BlockSchema> implements PluginView {
  private currentState: RangeSelectionState = {
    show: false,
    blockId: null,
    rangeStart: null,
    rangeEnd: null,
    referenceRect: null,
  }

  /**
   * While the user is pressing the mouse button to drag a new selection we
   * suppress the bubble so it does not flicker into view mid-drag.
   */
  private suppressWhileSelecting = false
  private settleTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private readonly editor: BlockNoteEditor<BSchema>,
    private readonly pmView: EditorView,
    private readonly onUpdate: (state: RangeSelectionState) => void,
  ) {
    this.pmView.dom.addEventListener('mousedown', this.onMouseDown)
    this.pmView.dom.addEventListener('mouseup', this.onMouseUp)
    // Mobile / touch: mirror the pointer lifecycle so the bubble settles after
    // the OS finishes adjusting the text selection handles.
    this.pmView.dom.addEventListener('touchstart', this.onMouseDown, {passive: true})
    this.pmView.dom.addEventListener('touchend', this.onMouseUp, {passive: true})
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private emitState(next: RangeSelectionState) {
    this.currentState = next
    this.onUpdate(next)
  }

  private hide() {
    if (this.currentState.show) {
      this.emitState({
        show: false,
        blockId: null,
        rangeStart: null,
        rangeEnd: null,
        referenceRect: null,
      })
    }
  }

  // ---------------------------------------------------------------------------
  // ProseMirror PluginView lifecycle
  // ---------------------------------------------------------------------------

  update(view: EditorView) {
    if (this.suppressWhileSelecting) return

    const {state} = view

    // Only activate in read-only mode; edit mode uses the FormattingToolbar.
    if (view.editable) {
      this.hide()
      return
    }

    const {selection} = state

    if (selection.empty || !isTextSelection(selection)) {
      this.hide()
      return
    }

    const {$from, $to} = selection
    const from = $from.pos
    const to = $to.pos

    // Locate the nearest addressable container that holds the selection.
    let blockNode: import('prosemirror-model').Node | null = null
    let blockBeforePos = 0

    try {
      const posInfo = getNearestBlockOrCellPos(state.doc, from)
      blockNode = posInfo.node
      blockBeforePos = posInfo.posBeforeNode
    } catch {
      this.hide()
      return
    }

    // Only show the bubble when the entire selection is within a single
    // addressable container (single block, or single cell when inside a table).
    try {
      const endBlockInfo = getNearestBlockOrCellPos(state.doc, to)
      if (endBlockInfo.posBeforeNode !== blockBeforePos) {
        this.hide()
        return
      }
    } catch {
      this.hide()
      return
    }

    const blockId: string | null = blockNode?.attrs?.id ?? null

    // Find the blockContent node start position (first child of blockNode or tableCell / tableHeader).
    let blockContentBeforePos = blockBeforePos
    blockNode.forEach((child, offset) => {
      if (child.type.spec.group === 'block') {
        blockContentBeforePos = blockBeforePos + offset + 1
      }
    })

    const rangeStart = prosemirrorPosToBlockTextOffset(state.doc, from, blockContentBeforePos)
    const rangeEnd = prosemirrorPosToBlockTextOffset(state.doc, to, blockContentBeforePos)

    const referenceRect = posToDOMRect(view, from, to)

    this.emitState({
      show: true,
      blockId,
      rangeStart,
      rangeEnd,
      referenceRect,
    })
  }

  destroy() {
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer)
    }
    this.pmView.dom.removeEventListener('mousedown', this.onMouseDown)
    this.pmView.dom.removeEventListener('mouseup', this.onMouseUp)
    this.pmView.dom.removeEventListener('touchstart', this.onMouseDown)
    this.pmView.dom.removeEventListener('touchend', this.onMouseUp)

    if (this.currentState.show) {
      this.emitState({
        show: false,
        blockId: null,
        rangeStart: null,
        rangeEnd: null,
        referenceRect: null,
      })
    }
  }

  // ---------------------------------------------------------------------------
  // Mouse handlers
  // ---------------------------------------------------------------------------

  onMouseDown = () => {
    // Suppress the bubble while the user is dragging a new selection.
    this.suppressWhileSelecting = true
    if (this.currentState.show) {
      this.emitState({
        show: false,
        blockId: null,
        rangeStart: null,
        rangeEnd: null,
        referenceRect: null,
      })
    }
  }

  onMouseUp = () => {
    // Give ProseMirror a tick to settle the selection before we evaluate it.
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer)
    }
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null
      this.suppressWhileSelecting = false
      this.update(this.pmView)
    }, SETTLE_DELAY_MS)
  }
}

// ---------------------------------------------------------------------------
// Public plugin class
// ---------------------------------------------------------------------------

/** ProseMirror plugin key for the RangeSelection plugin. */
export const rangeSelectionPluginKey = new PluginKey('RangeSelectionPlugin')

/**
 * ProseMirror plugin that shows a citation/comment bubble whenever the user
 * makes a non-empty text selection in **read-only** mode.
 *
 * In edit mode the standard FormattingToolbar is responsible for selection
 * actions, so this plugin stays dormant to avoid conflicts.
 *
 * @example
 * ```ts
 * const plugin = new RangeSelectionProsemirrorPlugin(editor)
 * plugin.onUpdate((state) => {
 *   if (state.show) {
 *     console.log('selected', state.blockId, state.rangeStart, state.rangeEnd)
 *   }
 * })
 * ```
 */
export class RangeSelectionProsemirrorPlugin<BSchema extends BlockSchema> extends EventEmitter<RangeSelectionEvents> {
  /** The raw ProseMirror plugin to register in the editor's plugin list. */
  public readonly plugin: Plugin

  constructor(private readonly editor: BlockNoteEditor<BSchema>) {
    super()

    this.plugin = new Plugin({
      key: rangeSelectionPluginKey,
      view: (editorView) => {
        return new RangeSelectionView(editor, editorView, (state) => {
          this.emit('update', state)
        })
      },
    })
  }

  /**
   * Subscribes to selection state updates.  Returns an unsubscribe function
   * that should be called on cleanup (e.g. in a React `useEffect` return).
   */
  public onUpdate(callback: (state: RangeSelectionState) => void): () => void {
    return this.on('update', callback)
  }
}
